import StorageService from "./StorageService.js";

/**
 * « Comment jouer ? » en popup (20 du TODO).
 *
 * Le bloc reste écrit dans le HTML de chaque page de jeu (L8 du plan SEO : un
 * robot sans JavaScript le lit tel quel, sous la zone de jeu). Au chargement,
 * il passe dans une fenêtre par-dessus la partie, la première fois seulement :
 * une fois fermée (croix, bouton, clic à côté, Échap), elle ne s'ouvre plus
 * d'elle-même pour cette page (`gameInfo.<slug>.hidden`, par langue comme les
 * autres préférences). Un petit bouton « ? » près du titre permet de la relire.
 *
 * Fermer la fenêtre avant d'avoir joué remet le chrono à zéro (événement
 * `geonerd:gameinfo-closed`, écouté par les jeux) : lire la règle ne doit pas
 * coûter de temps sur un mode classé.
 */
export default class GameInfo {
	/**
	 * La partie est finie (ou recommence) : la fenêtre se ferme et le bouton
	 * « ? » disparaît, ou revient. Rien ne se rouvre tout seul.
	 */
	static setVisible(visible) {
		const instance = GameInfo.instance;
		if (!instance) return;
		if (!visible) instance.close(false);
		if (instance.helpButton) instance.helpButton.hidden = !visible;
	}

	constructor() {
		this.section = document.querySelector(".game-info");
		if (!this.section) return;
		GameInfo.instance = this;

		const slug = document.querySelector(".page")?.classList[1];
		this.key = `gameInfo.${slug}.hidden`;
		this.t = (k) => window.geoNerdApp?.languageManager?.t(k) ?? k;

		this.buildOverlay();
		this.addHelpButton();

		if (!StorageService.load(this.key)) this.openWhenFree();
	}

	/** La section passe dans une fenêtre modale, avec une action principale. */
	buildOverlay() {
		this.overlay = document.createElement("div");
		this.overlay.className = "game-info-overlay";
		this.overlay.hidden = true;
		this.section.parentNode.insertBefore(this.overlay, this.section);
		this.overlay.appendChild(this.section);

		const title = this.section.querySelector("h2");
		if (title) {
			title.id = title.id || "game-info-title";
			this.section.setAttribute("aria-labelledby", title.id);
		}
		this.section.setAttribute("role", "dialog");
		this.section.setAttribute("aria-modal", "true");

		// Les cartes d'entraînement ont déjà leur bouton de validation (`.game-info-ok`).
		if (!this.section.querySelector(".game-info-ok")) {
			this.section.insertAdjacentHTML("beforeend",
				`<button type="button" class="btn game-info-ok game-info-go">${this.t("game.letsGo")}</button>`);
		}

		this.section.querySelectorAll(".game-info-close, .game-info-ok").forEach((btn) => {
			btn.addEventListener("click", () => this.close(true));
		});
		this.overlay.addEventListener("click", (e) => {
			if (e.target === this.overlay) this.close(true);
		});
		this.onKeyDown = (e) => {
			if (e.key === "Escape") this.close(true);
		};
	}

	/**
	 * Bouton « ? » pour relire la règle : dans le coin du bloc de titre, ou
	 * flottant sur les cartes d'entraînement, dont le titre n'est lu que par les
	 * lecteurs d'écran. Jamais dans le `<h1>` lui-même, dont le texte doit rester
	 * celui de la page.
	 */
	addHelpButton() {
		const label = this.section.querySelector("h2")?.textContent?.trim() || this.t("howToPlay.h1");
		const titleBlock = document.querySelector(".page .title-container");
		this.helpButton = document.createElement("button");
		this.helpButton.type = "button";
		this.helpButton.className = titleBlock ? "game-info-help" : "game-info-help is-floating";
		this.helpButton.setAttribute("aria-label", label);
		this.helpButton.title = label;
		this.helpButton.textContent = "?";
		this.helpButton.addEventListener("click", () => this.open());
		if (titleBlock) titleBlock.classList.add("has-help");
		(titleBlock || document.querySelector(".page") || document.body).appendChild(this.helpButton);
	}

	/**
	 * Première visite : la fenêtre s'ouvre, mais jamais par-dessus celle des
	 * nouveautés, qui peut s'ouvrir au même moment pour un joueur qui revient.
	 */
	openWhenFree() {
		const news = document.querySelector(".notifications-popup");
		if (!news || !news.classList.contains("show")) {
			this.open();
			return;
		}
		const observer = new MutationObserver(() => {
			if (news.classList.contains("show")) return;
			observer.disconnect();
			this.open();
		});
		observer.observe(news, { attributes: true, attributeFilter: ["class"] });
	}

	open() {
		if (!this.overlay || !this.overlay.hidden) return;
		this.overlay.hidden = false;
		document.body.classList.add("has-game-info");
		document.addEventListener("keydown", this.onKeyDown);
		(this.section.querySelector(".game-info-ok") || this.section).focus?.();
	}

	/** @param {boolean} remember la fermeture vaut « ne plus l'ouvrir de lui-même » */
	close(remember) {
		if (!this.overlay || this.overlay.hidden) return;
		this.overlay.hidden = true;
		document.body.classList.remove("has-game-info");
		document.removeEventListener("keydown", this.onKeyDown);
		if (remember) {
			StorageService.save(this.key, true);
			document.dispatchEvent(new CustomEvent("geonerd:gameinfo-closed"));
		}
	}
}
